// Language of the staff and owner screens (issue #21): the device's choice (`rr_lang`), written on <html> as `lang`
// and `dir` so the whole app mirrors in Arabic, and handed to api.ts so every call carries it as Accept-Language.
// The picker, the logo and the `Filled` helper live here so every screen renders them the same way and no screen
// imports App.tsx (which imports every screen).
import { Fragment, useEffect, useState } from 'react';
import { BRAND_NAME, LANG_DIR, LANG_LABELS, LANG_PICKER_ORDER, UI, fillParts, pickLang, type Lang } from '@ringring/core';
import { setRequestLang } from './api';

const LANG_KEY = 'rr_lang';

/** The language this device chose last; French when nothing is stored or storage is unavailable. */
export function storedLang(): Lang {
  try {
    return pickLang(localStorage.getItem(LANG_KEY), 'fr');
  } catch {
    return 'fr';
  }
}

/**
 * The screen language and its setter. The API client learns it before the first screen renders (screens fetch from
 * their effects, which run before this hook's effect), so a refusal comes back labelled in the right language.
 */
export function useLang(): [Lang, (l: Lang) => void] {
  const [lang, setLangState] = useState<Lang>(() => {
    const l = storedLang();
    setRequestLang(l);
    return l;
  });
  const setLang = (l: Lang) => {
    setRequestLang(l);
    setLangState(l);
    try {
      localStorage.setItem(LANG_KEY, l);
    } catch {
      // storage unavailable: the choice lasts for this page only
    }
  };
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = LANG_DIR[lang];
  }, [lang]);
  return [lang, setLang];
}

/**
 * العربية · Français · English, in that order on every screen: each label in its own script, tagged with its language
 * and direction, never a flag. The dots are decoration; the group is named in the screen language.
 */
export function LangPicker({ lang, setLang }: { lang: Lang; setLang: (l: Lang) => void }) {
  return (
    <div className="lang" role="group" aria-label={UI[lang].language}>
      {LANG_PICKER_ORDER.map((l, i) => (
        <Fragment key={l}>
          {i > 0 ? (
            <span className="sep" aria-hidden="true">
              ·
            </span>
          ) : null}
          <button type="button" lang={l} dir={LANG_DIR[l]} aria-pressed={l === lang} onClick={() => setLang(l)}>
            {LANG_LABELS[l]}
          </button>
        </Fragment>
      ))}
    </div>
  );
}

/** The wordmark: always the SVG, never typed, never mirrored; the alt text is the product name in Latin script. */
export function Logo({ reverse = false, className }: { reverse?: boolean; className?: string }) {
  return <img className={className} src={reverse ? '/logo-reverse.svg' : '/logo.svg'} alt={BRAND_NAME} />;
}

/**
 * A copy template with its variables filled in, each variable isolated as an LTR run (`<bdi dir="ltr">`) so a
 * number, a phone, an address or a URL keeps its characters in order inside Arabic text. `ltr` names the variables
 * to isolate, all of them by default; a variable that is text in the page language (a pickup point name) is left out.
 */
export function Filled({ template, vars, ltr }: { template: string; vars: Record<string, string>; ltr?: readonly string[] }) {
  return (
    <>
      {fillParts(template, vars).map((p, i) =>
        p.kind === 'var' && (ltr === undefined || ltr.includes(p.name)) ? (
          <bdi key={i} dir="ltr">
            {p.text}
          </bdi>
        ) : (
          <Fragment key={i}>{p.text}</Fragment>
        ),
      )}
    </>
  );
}
